import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './context/AuthContext';

import Sidebar from './components/layout/Sidebar';
import CreationPanel from './pages/ContentCreation/CreationPanel';

import ContentHistory from './pages/ContentHistory';
import HotspotPage from './pages/HotspotPage';
import SkillsPage from './pages/SkillsPage';

import RSSPage from './pages/RSSPage';
import AboutPage from './pages/AboutPage';
import UpdateLogPage from './pages/UpdateLogPage';
import SettingsPage from './pages/SettingsPage';

import AuthModal from './components/modals/AuthModal';
import RechargeModal from './components/modals/RechargeModal';
import ContentWizardModal from './pages/ContentCreation/ContentWizardModal';

export default function Dashboard() {
  const {
    user,
    token,
    logout,
    loading: authLoading,
    isAuthenticated: isLoggedIn,
    refreshProfile,
  } = useAuth();

  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('dashboard_active_tab') || 'studio');
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [prefillTopic, setPrefillTopic] = useState('');
  const [toast, setToast] = useState(null);
  const [wizardHotspot, setWizardHotspot] = useState(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem('dashboard_active_tab', activeTab);
  }, [activeTab]);

  const userCreditBalance = user?.points_balance || 0;

  const isStudioTab = useMemo(() => activeTab === 'studio', [activeTab]);

  const handleUseTopic = (topic) => {
    if (!topic) return;
    setPrefillTopic(topic);
    setActiveTab('studio');
  };

  const showToast = useCallback((message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const handleOpenWizard = useCallback((config) => {
    if (!token) {
      setShowAuthModal(true);
      return;
    }
    setWizardHotspot(config);
  }, [token]);

  const handleWizardComplete = useCallback(() => {
    setWizardHotspot(null);
    showToast('🎉 任务已创建，正在为你生成内容');
    setActiveTab('tasks');
  }, [showToast]);

  if (authLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-gray-50 font-sans text-gray-900">
      <Sidebar
        activeTab={activeTab === 'studio' ? 'create' : activeTab}
        setActiveTab={(tab) => {
          if (tab === 'create') setActiveTab('studio');
          else setActiveTab(tab);
        }}
        user={user}
        userPoints={userCreditBalance}
        isLoggedIn={isLoggedIn}
        setShowRecharge={setShowRechargeModal}
        setShowMenu={setShowMenu}
        showMenu={showMenu}
        handleLogout={() => {
          logout();
          setShowMenu(false);
        }}
        onLogin={() => setShowAuthModal(true)}
      />

      <main className="relative flex h-full flex-1 flex-col overflow-hidden">
        {isStudioTab && (
          <div className="h-full w-full overflow-y-auto bg-[#F8FAFC] p-4 md:px-10 md:py-8">
            <div className="mx-auto max-w-[1400px] min-h-full">
              <CreationPanel prefillTopic={prefillTopic} onGenerate={handleOpenWizard} isLoggedIn={isLoggedIn} />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {!isStudioTab && (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full w-full overflow-y-auto bg-white"
            >
              {activeTab === 'tasks' && <ContentHistory />}
              {activeTab === 'hotspot' && <HotspotPage onGenerateFromHotspot={handleOpenWizard} />}
              {activeTab === 'skills' && <SkillsPage onApplyTemplate={(t) => handleUseTopic(`关于${t?.title || ''}的内�?..`)} />}

              {activeTab === 'rss' && <RSSPage onGenerateFromArticle={() => setActiveTab('studio')} />}
              {activeTab === 'about' && <AboutPage setShowRecharge={setShowRechargeModal} />}
              {activeTab === 'changelog' && <UpdateLogPage />}
              {activeTab === 'settings' && <SettingsPage />}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {showRechargeModal && (
        <RechargeModal
          onClose={() => setShowRechargeModal(false)}
          authToken={token}
          API_BASE_URL={import.meta.env?.VITE_API_BASE_URL || ''}
          onPaymentSuccess={refreshProfile}
        />
      )}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <ContentWizardModal
        open={Boolean(wizardHotspot)}
        onClose={() => setWizardHotspot(null)}
        hotspot={wizardHotspot}
        token={token}
        onComplete={handleWizardComplete}
      />

      {/* 全局 Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className={`fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-xl px-5 py-3 text-sm font-medium shadow-lg backdrop-blur ${toast.type === 'error'
              ? 'bg-red-600/90 text-white'
              : toast.type === 'info'
                ? 'bg-gray-800/90 text-white'
                : 'bg-emerald-600/90 text-white'
              }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


